import { useEffect, useRef } from 'react';
import Card from '@/components/Card';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { twMerge } from 'tailwind-merge';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/auth.hooks';
import { TVehiculeCheckResultSchema } from '@/schemas/vehicule-check-result/vehicule-check-result.schema';
import Loader from '@/components/Loading/Loader';
import { ResultPhotoList } from './ResultPhotoList';
import { TDamageSchema } from '@/schemas/damage/damage.schema';
import { TEquipmentSchema } from '@/schemas/equipment/equipment.schema';
import { TVehiculeSchema } from '@/schemas/vehicule/vehicule.schema';
import {
  ResultPhotoSubTab,
  ResultPhotoTab,
} from '@/type/ui/tab/vehiculeCheckResultTab';
import { useGetChildSubTab, useGetChildTab } from '@/hooks/tab.hooks';
import {
  removedUrlQueries,
  CHILD_SUB_TAB_QUERY_KEY,
  PDF_REDIRECT_QUERY_KEYS,
} from '@/utils/url-query.utils';
import { TFunction } from 'i18next';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import {
  usePreloadCheckpointImages,
  useResultImageList,
} from '../hooks/result-image.hooks';
import {
  getDamagesWithEquipement,
  getAllDamagesWithEquipementFirst,
} from '../utils/result-image.utils';
import { VehiculeCheckProgressIndicator } from '@/components/Custom/ProgressIndicator/VehiculeCheckProgressIndicator';
import { useVehiculeCheckVideoSockets } from '@/hooks/sockets/vehicule-check-video-sockets.hooks';
import { NotFound } from '@/components/Message/NotFound';
import { scrollToElement } from '@/utils/scroll.utils';

interface VehiculeCheckResultPhotosProps {
  t?: TFunction<undefined>;
  isLoading?: boolean;
  vehiculeCheckResult?: TVehiculeCheckResultSchema;
  moderationStatus: string;
}

export const VehiculeCheckResultPhotos = ({
  t,
  isLoading,
  vehiculeCheckResult,
  moderationStatus,
}: VehiculeCheckResultPhotosProps) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const urlResultObjectId = searchParams.get('resultObjectId');

  const { loggedInUser } = useAuth();
  const hasScrolledToUrlResultObjectRef = useRef(false);

  const { currentTab, navigateToTab } = useGetChildTab({
    defaultTab: ResultPhotoTab.EQUIPMENTS,
  });

  const { currentTab: currentSubTab, navigateToTab: navigateToSubTab } =
    useGetChildSubTab({
      defaultTab: ResultPhotoSubTab.ALL,
    });

  /**
   * === Hook for Vehicule Check Video Sockets ===
   */
  const {
    vehiculeCheckProgressStatus,
    videoUploadProgressPercent,
    isVideoProgressCompleted,
  } = useVehiculeCheckVideoSockets({
    vehiculeCheckId: id,
  });
  /**
   * === END OF Hook for Vehicule Check Video Sockets ===
   */

  /**
   * === Remove childSubTab if current tab is not DAMAGES ===
   */
  useEffect(() => {
    if (currentTab !== ResultPhotoTab.DAMAGES) {
      removedUrlQueries({
        queryKeys: CHILD_SUB_TAB_QUERY_KEY,
      });
    }
  }, [currentTab]);
  /**
   * === END OF Remove childSubTab if current tab is not DAMAGES ===
   */

  // Preload Checkpoint Images
  usePreloadCheckpointImages(isLoading, vehiculeCheckResult?.results);

  const equipmentsCheckPointId = vehiculeCheckResult?.results?.find(
    (result) => result?.checkPoint?.equipments,
  )?.checkPoint?.id;

  const damagesCheckPointId = vehiculeCheckResult?.results?.find(
    (result) => result?.checkPoint?.damages,
  )?.checkPoint?.id;

  const carsCheckPointId = vehiculeCheckResult?.results?.find(
    (result) => result?.checkPoint?.vehicules,
  )?.checkPoint?.id;

  const equipmentList = useResultImageList(
    vehiculeCheckResult?.results,
    'equipments',
  );

  let damageList = useResultImageList(vehiculeCheckResult?.results, 'damages');
  const damagesWithEquipement = getDamagesWithEquipement(damageList);

  damageList =
    currentSubTab === ResultPhotoSubTab.ALL
      ? getAllDamagesWithEquipementFirst(damageList)
      : damagesWithEquipement;

  const vehiculeList = useResultImageList(
    vehiculeCheckResult?.results,
    'vehicules',
  );

  /**
   * === Scroll to the photo if urlResultObjectId is present ===
   * (the photo preview modal is opened by ResultPhotoList)
   */
  useEffect(() => {
    if (
      !urlResultObjectId ||
      isLoading ||
      !vehiculeCheckResult ||
      !isVideoProgressCompleted ||
      hasScrolledToUrlResultObjectRef.current
    ) {
      return;
    }

    hasScrolledToUrlResultObjectRef.current = true;

    // Wait for React to render the photo list
    setTimeout(() => {
      const element = document.getElementById(
        `result-photo-preview-modal-${urlResultObjectId}`,
      );
      if (element) {
        const headerOffset = loggedInUser ? 600 : 535;
        scrollToElement({ element, headerOffset });
      }
    }, 300);
  }, [
    urlResultObjectId,
    isLoading,
    vehiculeCheckResult,
    isVideoProgressCompleted,
    loggedInUser,
  ]);
  /**
   * === END OF Scroll to the photo if urlResultObjectId is present ===
   */

  return (
    <Card
      title={
        <TitleSection
          text={t('common:photos')}
          defaultIconClassName="ki-picture"
        />
      }
      bodyClassName="py-8 flex flex-col gap-5"
    >
      {!vehiculeCheckResult ? (
        <div className="mt-7 mb-6">
          <NotFound text={`${t('resultsNotFound')}...`} />
        </div>
      ) : !isVideoProgressCompleted ? (
        <VehiculeCheckProgressIndicator
          t={t}
          progress={videoUploadProgressPercent}
          vehiculeCheckProgressStatus={vehiculeCheckProgressStatus}
        />
      ) : (
        <>
          <TabList className="gap-3.5">
            <TabItem
              className={twMerge(
                '',
                currentTab !== ResultPhotoTab.EQUIPMENTS && 'border-black/50',
              )}
              text={t('common:equipmentPhotos')}
              leftIcon={<i className="ki-outline ki-abstract-41" />}
              active={currentTab === ResultPhotoTab.EQUIPMENTS}
              onChange={() => {
                navigateToTab(ResultPhotoTab.EQUIPMENTS);
                // removedUrlQueries({
                //   queryKeys: [
                //     ...CHILD_SUB_TAB_QUERY_KEY,
                //     ...PDF_REDIRECT_QUERY_KEYS,
                //   ],
                // });
                removedUrlQueries({
                  queryKeys: PDF_REDIRECT_QUERY_KEYS,
                });
              }}
            />
            <TabItem
              className={twMerge(
                '',
                currentTab !== ResultPhotoTab.DAMAGES && 'border-black/50',
              )}
              text={t('common:damagePhotos')}
              leftIcon={<i className="ki-outline ki-bucket-square" />}
              active={currentTab === ResultPhotoTab.DAMAGES}
              onChange={() => {
                navigateToTab(ResultPhotoTab.DAMAGES);
                removedUrlQueries({
                  queryKeys: PDF_REDIRECT_QUERY_KEYS,
                });
              }}
            />
            <TabItem
              className={twMerge(
                '',
                currentTab !== ResultPhotoTab.VEHICULES && 'border-black/50',
              )}
              text={t('common:vehiculePhotos')}
              leftIcon={<i className="ki-outline ki-parcel" />}
              active={currentTab === ResultPhotoTab.VEHICULES}
              onChange={() => {
                navigateToTab(ResultPhotoTab.VEHICULES);
                // removedUrlQueries({
                //   queryKeys: [
                //     ...CHILD_SUB_TAB_QUERY_KEY,
                //     ...PDF_REDIRECT_QUERY_KEYS,
                //   ],
                // });
                removedUrlQueries({
                  queryKeys: PDF_REDIRECT_QUERY_KEYS,
                });
              }}
            />
          </TabList>
          {currentTab === ResultPhotoTab.EQUIPMENTS &&
            (isLoading ? (
              <Loader />
            ) : (
              <ResultPhotoList<TEquipmentSchema>
                t={t}
                activeTab={currentTab}
                hidePhotoActionButtons
                checkPointId={equipmentsCheckPointId}
                vehiculeCheckResult={vehiculeCheckResult}
                hideAddImage
                data={equipmentList as TEquipmentSchema[]}
                title={t('common:equipments')}
                notFoundText={
                  equipmentsCheckPointId
                    ? `${t('equipmentPhotosNotFound')}...`
                    : `${t('equipmentCheckPointNotFound')}...`
                }
                moderationStatus={moderationStatus}
                urlResultObjectId={urlResultObjectId}
              />
            ))}
          {currentTab === ResultPhotoTab.DAMAGES &&
            (isLoading ? (
              <Loader />
            ) : (
              <ResultPhotoList<TDamageSchema>
                t={t}
                activeTab={currentTab}
                childSubTabList={
                  <>
                    <TabItem
                      className={twMerge(
                        '[&.active]:!bg-purple-A713F6/85 [&.active]:!border-purple-A713F6/85',
                        currentSubTab !== ResultPhotoSubTab.ALL &&
                          'border-dashed border-black',
                      )}
                      text={t('common:allDamages')}
                      leftIcon={<i className="ki-outline ki-abstract-26" />}
                      active={currentSubTab === ResultPhotoSubTab.ALL}
                      onChange={() => {
                        navigateToSubTab(ResultPhotoSubTab.ALL);
                        removedUrlQueries({
                          queryKeys: PDF_REDIRECT_QUERY_KEYS,
                        });
                      }}
                    />
                    <TabItem
                      className={twMerge(
                        '[&.active]:!bg-purple-A713F6/85 [&.active]:!border-purple-A713F6/85',
                        currentSubTab !==
                          ResultPhotoSubTab.DAMAGES_WITH_EQUIPMENT &&
                          'border-dashed border-black',
                      )}
                      text={t('damagesWithEquipment')}
                      leftIcon={<i className="ki-outline ki-abstract-21" />}
                      active={
                        currentSubTab ===
                        ResultPhotoSubTab.DAMAGES_WITH_EQUIPMENT
                      }
                      onChange={() => {
                        navigateToSubTab(
                          ResultPhotoSubTab.DAMAGES_WITH_EQUIPMENT,
                        );
                        removedUrlQueries({
                          queryKeys: PDF_REDIRECT_QUERY_KEYS,
                        });
                      }}
                    />
                  </>
                }
                hidePhotoActionButtons
                checkPointId={damagesCheckPointId}
                vehiculeCheckResult={vehiculeCheckResult}
                hideAddImage
                data={damageList as TDamageSchema[]}
                title={t('common:damages')}
                notFoundText={
                  damagesCheckPointId
                    ? currentSubTab === ResultPhotoSubTab.ALL
                      ? `${t('damagePhotosNotFound')}...`
                      : `${t('damageOnEquipmentPhotosNotFound')}...`
                    : currentSubTab === ResultPhotoSubTab.ALL
                      ? `${t('damageCheckPointNotFound')}...`
                      : `${t('damageOnEquipmentCheckPointNotFound')}...`
                }
                moderationStatus={moderationStatus}
                urlResultObjectId={urlResultObjectId}
              />
            ))}
          {currentTab === ResultPhotoTab.VEHICULES &&
            (isLoading ? (
              <Loader />
            ) : (
              <ResultPhotoList<TVehiculeSchema>
                t={t}
                activeTab={currentTab}
                hidePhotoActionButtons
                checkPointId={carsCheckPointId}
                vehiculeCheckResult={vehiculeCheckResult}
                hideAddImage
                data={vehiculeList as TVehiculeSchema[]}
                title={t('common:vehicules')}
                notFoundText={
                  carsCheckPointId
                    ? `${t('vehiculePhotosNotFound')}...`
                    : `${t('vehiculeCheckPointNotFound')}...`
                }
                moderationStatus={moderationStatus}
                urlResultObjectId={urlResultObjectId}
              />
            ))}
        </>
      )}
    </Card>
  );
};
